/**
 * Startup audit hook — detects any Dexie rows with userId === 'local-user' and
 * reports them via PostHog so we can monitor whether the M82 fix holds in production.
 *
 * Runs once per authenticated session (module-level `hasRun` guard prevents
 * repeated execution on re-renders or React Strict-Mode double-invocations).
 * Uses requestIdleCallback with a setTimeout(0) fallback so it never blocks the
 * first paint.
 *
 * ADR-033: this hook is detection + telemetry only. Recovery UI (B3) is deferred
 * to a follow-up if telemetry shows recurrence.
 */

import { useEffect } from 'react';

import { db } from '@/services/storage/db';
import { posthogService } from '@/services/posthog-service';

// Sentinel value — must match dexie-adapter.ts LEGACY_PRE_AUTH_USER_ID string.
// Hard-coded here so this hook does NOT import from dexie-adapter (import allowlist).
const LEGACY_SENTINEL = 'local-user';

// Tables that carry a userId column and participate in sync.
// Must stay in sync with the entity set in sync-service.ts push loops.
const SYNCABLE_USER_ID_TABLES = [
  'properties',
  'seasons',
  'hunts',
  'huntSits',
  'harvestRecords',
  'huntJournalEntries',
  'photos',
] as const;

// Module-level flag — ensures the audit runs at most once per authenticated session.
// Resets when the page is hard-reloaded (module is re-evaluated), which is the correct
// lifetime — a fresh reload should audit again to catch any rows written between sessions.
let hasRun = false;

async function runAudit(): Promise<void> {
  for (const tableName of SYNCABLE_USER_ID_TABLES) {
    // CAST: all tables in SYNCABLE_USER_ID_TABLES are confirmed to exist on
    // DexieAdapter and carry a userId index. The cast is necessary because Dexie
    // Table types don't expose a generic `.where()` API via the table-name accessor.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const table = (db as any)[tableName] as import('dexie').Table<{ userId: string }, string>;
    if (!table) continue;

    const count = await table
      .where('userId')
      .equals(LEGACY_SENTINEL)
      .count();

    if (count > 0) {
      posthogService.track('local_user_row_detected', { table: tableName, count });
      console.error(
        `[use-local-user-audit] Found ${count} row(s) with userId='local-user' in table "${tableName}".`,
        'These rows were created by a pre-M82 code path and cannot be synced.',
        'They will be remediated if the B3 recovery UI is implemented (see ADR-033).',
      );
    }
  }
}

/**
 * Schedule the audit via requestIdleCallback (or setTimeout fallback) so it does
 * not block the authenticated app shell from rendering.
 */
function scheduleAudit(): void {
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(() => {
      runAudit().catch((err: unknown) => {
        console.error('[use-local-user-audit] Audit failed:', err);
      });
    });
  } else {
    setTimeout(() => {
      runAudit().catch((err: unknown) => {
        console.error('[use-local-user-audit] Audit failed:', err);
      });
    }, 0);
  }
}

/**
 * Wire into AuthGuard (authenticated branch) via:
 *   useLocalUserAudit();
 *
 * The hook is a no-op if the user is not authenticated or the audit has already run.
 *
 * @param isAuthenticated - pass `true` only when `useAuthStore().status === 'authenticated'`
 */
export function useLocalUserAudit(isAuthenticated: boolean): void {
  useEffect(() => {
    if (!isAuthenticated) return;
    if (hasRun) return;

    hasRun = true;
    scheduleAudit();
  }, [isAuthenticated]);
}
